// Kepabeanan › Buat Aju: pick the document type to start (revamp PR 7a). Same cards, links, email-based locks, tour
// anchors and copy as the framer-motion page it replaces; Lumen tokens, and a CSS stagger (off under reduced motion).
import {
  ArrowDownLeft,
  ArrowLeftRight,
  ArrowRight,
  ArrowUpRight,
  Award,
  FileInput,
  FileOutput,
  Lock,
  LogIn,
  LogOut,
  Package,
  PackageOpen,
  Ship,
  Sparkles,
  Truck,
  Warehouse,
} from 'lucide-react';
import type * as React from 'react';
import { useNavigate } from 'react-router-dom';

import { Breadcrumb, BreadcrumbItem, BreadcrumbList } from '@/components/lumen/breadcrumb';
import { PageHeader, PageShell } from '@/components/shell/PageHeader';
import { cn } from '@/lib/utils';
import { useLanguage } from '@contexts/LanguageContext';

// Email-based edit access rules (only users with 'edit' can create new documents)
const BC20_EDIT_EMAILS = [
  'christina.vonny@molgroup.com',
  'ari.wibowo@molgroup.com',
  'femy.muthia@molgroup.com',
  'lanny.apriliany@molgroup.com',
  'mlg-id.seaimport01@molgroup.com',
  'mlg-id.airport01@molgroup.com',
];

const BC30_EDIT_EMAILS = [
  'christina.vonny@molgroup.com',
  'ari.wibowo@molgroup.com',
  'femy.muthia@molgroup.com',
  'reza.haqqa@molgroup.com',
  'ratri.puji@molgroup.com',
  'prama.adi@molgroup.com',
  'mlg-id.airexport01@molgroup.com',
];

const getUserEmail = (): string => {
  try {
    const raw = localStorage.getItem('authData');
    if (!raw) return '';
    const parsed = JSON.parse(raw);
    return (parsed?.email || '').toLowerCase();
  } catch {
    return '';
  }
};

/** A card's accent while it is available: import violet and export green (the shipment-type tones), the rest blue. */
type Tone = 'ai' | 'ok' | 'info';
const TONE: Record<Tone, { icon: string; chip: string; action: string }> = {
  ai: { icon: 'bg-ai/10 text-ai', chip: 'border-ai/25 bg-ai/10 text-ai-ink', action: 'text-ai' },
  ok: { icon: 'bg-ok/10 text-ok', chip: 'border-ok/25 bg-ok/10 text-ok-ink', action: 'text-ok' },
  info: { icon: 'bg-label/10 text-label', chip: 'border-label/25 bg-label/10 text-info-ink', action: 'text-label' },
};

interface Product {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  available: boolean;
  link?: string;
  badge?: string;
  badgeVariant?: 'solid' | 'dashed';
  tone: Tone;
  code: string;
  /** Document family labels shown on the card (e.g. BC 2.0 / BC 3.0) */
  docCodes?: string[];
}

export default function BuatAjuPage() {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const userEmail = getUserEmail();
  const canEditImport = !userEmail.endsWith('@molgroup.com') || BC20_EDIT_EMAILS.includes(userEmail);
  const canEditExport = !userEmail.endsWith('@molgroup.com') || BC30_EDIT_EMAILS.includes(userEmail);

  const soon = (id: string, title: string, icon: React.ReactNode, code: string, docCode = title): Product => ({
    id,
    title,
    description: t(`buataju.${id}Desc`),
    icon,
    available: false,
    badge: t('buataju.comingSoon'),
    tone: 'info',
    code,
    docCodes: [docCode],
  });

  const primaryProducts: Product[] = [
    {
      id: 'import',
      title: t('buataju.import'),
      description: t('buataju.importDesc'),
      icon: <FileInput />,
      available: canEditImport,
      link: '/doc-pabean/upload?type=import',
      badge: canEditImport ? t('buataju.popular') : t('buataju.limitedAccess'),
      tone: 'ai',
      code: t('buataju.codeImport'),
      docCodes: ['BC 2.0'],
    },
    {
      id: 'export',
      title: t('buataju.export'),
      description: t('buataju.exportDesc'),
      icon: <FileOutput />,
      available: canEditExport,
      link: '/doc-pabean/upload?type=export',
      badge: canEditExport ? t('buataju.popular') : t('buataju.limitedAccess'),
      tone: 'ok',
      code: t('buataju.codeExport'),
      docCodes: ['BC 3.0'],
    },
    soon('bc33', 'BC 3.3', <PackageOpen />, t('buataju.codeEkspor')),
    {
      id: 'ppftz',
      title: 'PPFTZ',
      description: t('buataju.ppftzDesc'),
      icon: <Warehouse />,
      available: true,
      link: '/doc-pabean/upload?type=ppftz',
      badge: t('buataju.new'),
      badgeVariant: 'dashed',
      tone: 'info',
      code: t('buataju.codePpftz'),
    },
  ];

  // TPB products from Beacukai OpenAPI (3.TPB) — unique Schema/Payload pairs
  const tpb = t('buataju.codeTpb');
  const tpbProducts: Product[] = [
    soon('bc16', 'BC 1.6', <Ship />, tpb),
    soon('bc23', 'BC 2.3', <Truck />, tpb),
    soon('bc25', 'BC 2.5', <PackageOpen />, tpb),
    soon('bc261', 'BC 2.6.1', <ArrowUpRight />, tpb),
    soon('bc262', 'BC 2.6.2', <ArrowDownLeft />, tpb),
    soon('bc27', 'BC 2.7', <ArrowLeftRight />, tpb),
    soon('bc28', 'BC 2.8', <Package />, tpb),
    soon('bc40', 'BC 4.0', <LogIn />, tpb),
    soon('bc41', 'BC 4.1', <LogOut />, tpb),
    soon('bcp3bet', 'BC P3BET', <Award />, tpb),
  ];

  const products = [...primaryProducts, ...tpbProducts];

  const handleSelect = (p: Product) => {
    if (!p.available || !p.link) return;
    navigate(p.link);
  };

  const renderCard = (p: Product, index: number) => {
    const disabled = !p.available || !p.link;
    const tone = TONE[p.tone];
    return (
      <div
        key={p.id}
        data-tour={`buat-aju-${p.id}`}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        onClick={() => handleSelect(p)}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleSelect(p);
          }
        }}
        style={{ animationDelay: `${index * 60}ms` }}
        className={cn(
          'group relative flex min-h-[270px] flex-col rounded-panel border border-line p-[22px] outline-none',
          'fill-mode-both motion-safe:fade-in-0 motion-safe:slide-in-from-bottom-2 motion-safe:animate-in motion-safe:duration-300',
          'transition-[box-shadow,border-color,translate] duration-200 ease-out focus-visible:ring-[3px] focus-visible:ring-label/40',
          disabled
            ? 'cursor-not-allowed bg-surface-2'
            : 'cursor-pointer bg-raised hover:border-line-strong hover:shadow-overlay motion-safe:hover:-translate-y-[3px]'
        )}
      >
        {/* Top: icon + badge */}
        <div className="flex items-start justify-between gap-2.5">
          <div
            className={cn(
              'flex size-[46px] shrink-0 items-center justify-center rounded-card [&_svg]:size-[22px]',
              disabled ? 'bg-surface text-fg-muted' : tone.icon
            )}
          >
            {p.icon}
          </div>
          {p.badge && (
            <span
              className={cn(
                'inline-flex items-center gap-1 rounded-full border px-[9px] py-1 font-semibold text-[11px] leading-none [&_svg]:size-[11px]',
                disabled
                  ? 'border-transparent bg-surface text-fg-muted'
                  : p.badgeVariant === 'dashed'
                    ? cn('border-dashed bg-raised', tone.chip)
                    : tone.chip
              )}
            >
              {disabled && <Lock aria-hidden />}
              {!disabled && p.badge === t('buataju.popular') && <Sparkles aria-hidden />}
              {p.badge}
            </span>
          )}
        </div>

        {/* Content */}
        <div className="mt-5">
          <h3 className={cn('mb-[7px] font-bold text-[17px] tracking-tight', disabled ? 'text-fg-muted' : 'text-fg')}>
            {p.title}
          </h3>
          <p className={cn('m-0 text-[13.5px] leading-[1.55]', disabled ? 'text-fg-muted' : 'text-fg-2')}>
            {p.description}
          </p>
          {p.docCodes && p.docCodes.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {p.docCodes.map((docCode) => (
                <span
                  key={docCode}
                  className={cn(
                    'inline-flex items-center rounded-tag border px-2 py-[3px] font-mono font-semibold text-[11px] tracking-wide',
                    disabled ? 'border-transparent bg-surface text-fg-muted' : tone.chip
                  )}
                >
                  {docCode}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-auto flex items-center justify-between border-line border-t pt-[22px]">
          <span className="font-semibold text-[11px] text-fg-muted uppercase tracking-[0.06em]">{p.code}</span>
          <span
            className={cn(
              'inline-flex items-center gap-[5px] font-bold text-13 transition-[gap] duration-200 group-hover:gap-2',
              disabled ? 'text-fg-muted' : tone.action
            )}
          >
            {disabled ? (
              t('buataju.locked')
            ) : (
              <>
                {t('buataju.start')}
                <ArrowRight aria-hidden className="size-[15px] transition-transform group-hover:translate-x-0.5" />
              </>
            )}
          </span>
        </div>
      </div>
    );
  };

  const grid = 'grid grid-cols-1 gap-[18px] sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4';

  return (
    <PageShell className="mx-auto w-full max-w-[1240px]">
      <PageHeader
        breadcrumbs={
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>{t('menu.kepabeanan')}</BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        }
        meta={
          <span className="inline-flex items-center gap-1.5 rounded-full border border-line bg-raised px-2.5 py-1 font-semibold text-fg-2 text-xs">
            <Sparkles aria-hidden className="size-3.5 text-label" />
            {t('buataju.badge')}
          </span>
        }
        title={t('buataju.title')}
        description={t('buataju.subtitle')}
        actions={
          <span className="inline-flex items-center gap-2 whitespace-nowrap rounded-card border border-line bg-raised px-3.5 py-2 font-semibold text-13 text-fg">
            <span className="size-2 rounded-full bg-ok shadow-[0_0_0_3px_color-mix(in_srgb,var(--lm-ok)_15%,transparent)]" />
            {products.length} {t('buataju.typesAvailable')}
          </span>
        }
      />

      {/* Primary cards */}
      <div data-tour="buat-aju-grid" className={grid}>
        {primaryProducts.map((p, i) => renderCard(p, i))}
      </div>

      {/* TPB section */}
      <section className="mt-5">
        <div className="mb-4 flex items-center gap-3">
          <h2 className="m-0 font-bold text-[15px] text-fg tracking-tight">{t('buataju.tpb')}</h2>
          <span className="rounded-full bg-surface-2 px-2.5 py-0.5 font-semibold text-[11px] text-fg-muted">
            {t('buataju.tpbDesc')}
          </span>
          <div className="h-px flex-1 bg-line" />
        </div>
        <div className={grid}>{tpbProducts.map((p, i) => renderCard(p, i + primaryProducts.length))}</div>
      </section>
    </PageShell>
  );
}
